import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { UnsubscribeModal } from "./UnsubscribeModal";

export function Footer() {
    const brand = useBrand();
    const [unsubOpen, setUnsubOpen] = useState(false);

    return (
        <footer className="mt-24 border-t border-line bg-surface-sunken" data-testid="footer">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                    {/* Brand / contact */}
                    <div className="col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-2.5">
                            <span className="grid place-items-center w-8 h-8 rounded-lg bg-accent-sheen">
                                <span className="w-2.5 h-2.5 rotate-45 rounded-[2px] bg-canvas" />
                            </span>
                            <span className="font-display text-lg font-bold tracking-[-0.02em]">{brand.site_name}</span>
                        </div>
                        {brand.tagline && <p className="mt-4 text-sm text-dim leading-relaxed max-w-xs">{brand.tagline}</p>}
                        <p className="mt-5 font-mono text-xs text-dim" data-testid="footer-contact-email">{brand.email}</p>
                        {brand.phone && <p className="mt-1 font-mono text-xs text-dim">{brand.phone}</p>}
                    </div>

                    {/* Sections */}
                    <div>
                        <h5 className="eyebrow text-dim/70 mb-4">Sections</h5>
                        <ul className="space-y-2.5">
                            {CATEGORIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/category/${c}`} className="text-sm text-dim hover:text-accent transition-colors duration-150">
                                        {CATEGORY_LABELS[c]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company */}
                    <div>
                        <h5 className="eyebrow text-dim/70 mb-4">Company</h5>
                        <ul className="space-y-2.5">
                            <li><Link to="/advertise" data-testid="footer-advertise" className="text-sm text-dim hover:text-accent transition-colors duration-150">Advertise with us</Link></li>
                            <li><Link to="/archive" className="text-sm text-dim hover:text-accent transition-colors duration-150">Archive</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="text-sm text-dim hover:text-accent transition-colors duration-150">Privacy Policy</Link></li>
                            <li><Link to="/terms" data-testid="footer-terms" className="text-sm text-dim hover:text-accent transition-colors duration-150">Terms of Use</Link></li>
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div className="panel p-6">
                        <h5 className="eyebrow text-accent mb-3" data-testid="footer-newsletter-title">Newsletter</h5>
                        <p className="text-sm text-dim leading-relaxed">Get the latest financial news delivered to your inbox.</p>
                        <Link
                            to="/register"
                            data-testid="footer-newsletter-subscribe"
                            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-[13px] font-semibold hover:shadow-glow hover:brightness-110 transition-[filter,box-shadow] duration-150"
                        >
                            Subscribe
                            <ArrowUpRight size={14} />
                        </Link>
                        <button
                            onClick={() => setUnsubOpen(true)}
                            data-testid="footer-unsubscribe"
                            className="mt-4 block text-xs text-dim/70 hover:text-neg transition-colors duration-150"
                        >
                            Unsubscribe
                        </button>
                    </div>
                </div>

                <div className="rule mt-12" />
                <p className="mt-6 font-mono text-xs text-dim/60">
                    © {new Date().getFullYear()} {brand.site_name} · {brand.domain}
                </p>
            </div>
            <UnsubscribeModal open={unsubOpen} onOpenChange={setUnsubOpen} />
        </footer>
    );
}
